import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAllScopeGitLinks } from '../../services/accountsStore';
import { isCurrentScope, scopeFolder } from '../../services/git/scopeRepo';
import { isTauri } from '../../utils/tauri';
import { useNotificationStore } from '../../store/notificationStore';
import ItemHistoryModal from './ItemHistoryModal';
import { gitWords } from '../../services/git/gitSyncStatus';

/**
 * An item's git history (git sync Stage 2, S2.7), for whoever offers the way
 * in: the clock button in a page's header, or the task window's "…" menu
 * (owner, 06.10.2026). `available` is true only when the item's scope syncs to
 * a repository — or is tracked on this device (T10); `modal` must be rendered.
 *
 * @param {{ scopeKey: string, itemId: string, itemName?: string, kind: 'page'|'task' }} args
 * @returns {{ available: boolean, open: () => void, modal: React.ReactNode }}
 */
export default function useScopeItemHistory({ scopeKey, itemId, itemName, kind }) {
  const [git, setGit] = useState(null);
  const [open, setOpen] = useState(false);
  const usable = isTauri() && itemId != null && scopeFolder(scopeKey) && isCurrentScope(scopeKey);

  useEffect(() => {
    if (!usable) return undefined;
    let alive = true;
    getAllScopeGitLinks()
      .then((links) => alive && setGit(links[scopeKey] || null))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [usable, scopeKey, open]);

  const source = useMemo(() => {
    const scope = { key: scopeKey, git };
    // Tracked on this device only (T10): "Save version" words, not "Sync now".
    const local = git?.provider === 'local';
    return {
      noun: kind,
      layout: 'scope',
      ...(local ? { words: gitWords({ git }) } : {}),
      load: async () => (await import('../../services/git/scopeItemHistory')).loadScopeItemHistory(scope, itemId, kind),
      restore: async (path, content) => {
        await (await import('../../services/git/scopeItemHistory')).restoreScopeItem(scope, path, content);
        useNotificationStore
          .getState()
          .success(`The old version is back. ${local ? 'Save version records it.' : 'Sync now sends it.'}`, itemName || 'Restored');
      },
    };
  }, [scopeKey, git, itemId, kind, itemName]);

  const openHistory = useCallback(() => setOpen(true), []);
  return {
    available: Boolean(usable && git),
    open: openHistory,
    modal: open ? <ItemHistoryModal isOpen onClose={() => setOpen(false)} itemId={itemId} itemName={itemName} source={source} /> : null,
  };
}
